import { cn } from '@ifixit/ui/lib/utils';
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
} from '@ifixit/ui/components/dropdown-menu';
import { FaIcon } from '@ifixit/ui/components/icon';
import { SentryError } from '@ifixit/sentry';
import isEqual from 'lodash/isEqual';
import {
   type ComponentProps,
   type ElementType,
   type ReactElement,
   type RefObject,
   memo,
   useCallback,
   useState,
} from 'react';
import { hiddenWrapContainerClasses, useHiddenWrap } from './FlexHiddenWrap';

export type BreadcrumbItem = {
   label: string;
   url?: string;
   onClick?: () => void;
};

type SharedBreadCrumbsProps = {
   breadcrumbIcon?: ReactElement;
   /** Renders the crumbs that have a `url`, such as Next.js's `SmartLink`. */
   linkComponent?: ElementType;
   /** schema.org microdata: for a page's main trail, unless the page has breadcrumb JSON-LD. */
   includeSchema?: boolean;
} & ComponentProps<'div'>;

export type BreadCrumbsProps = SharedBreadCrumbsProps & {
   breadCrumbs: BreadcrumbItem[];
   breadcrumbsToShow?: number;
};

export type StaticBreadCrumbsProps = SharedBreadCrumbsProps & {
   visibleBreadCrumbs: BreadcrumbItem[];
   collapsedBreadCrumbs?: BreadcrumbItem[];
   listRef?: RefObject<HTMLOListElement | null>;
};

type IFixitBreadcrumbItemProps = BreadcrumbItem & {
   /** 1-based position from the top of the hierarchy, for the schema.org meta. */
   position: number;
   isLast: boolean;
   includeSchema: boolean;
   linkComponent: ElementType;
   breadcrumbIcon: ReactElement;
};

export const BreadCrumbs = memo(function DynamicBreadCrumbs({
   breadCrumbs,
   breadcrumbsToShow,
   ...staticProps
}: BreadCrumbsProps) {
   if (breadcrumbsToShow != undefined && breadcrumbsToShow <= 0) {
      throw new SentryError('breadcrumbsToShow can only be undefined or must be greater than 0');
   }

   const isStatic = !!breadcrumbsToShow;

   const visibleBreadCrumbs = isStatic
      ? breadCrumbs.slice(breadCrumbs.length - breadcrumbsToShow)
      : breadCrumbs;

   const hiddenBreadCrumbs = isStatic
      ? breadCrumbs.slice(0, breadCrumbs.length - breadcrumbsToShow)
      : [];

   const [wrappedBreadcrumbs, setWrappedBreadcrumbs] =
      useState<BreadcrumbItem[]>(hiddenBreadCrumbs);

   const wrappedChildrenEffect = useCallback(
      (children: HTMLElement[]) => {
         if (isStatic) {
            return;
         }
         const wrappedNames = new Set(children.map(c => c.dataset.name));
         const wrapped = breadCrumbs.filter(b => wrappedNames.has(b.label));
         setWrappedBreadcrumbs(() => wrapped);
      },
      [breadCrumbs, isStatic]
   );

   const { ref: listRef } = useHiddenWrap<HTMLOListElement>({ wrappedChildrenEffect });

   return (
      <StaticBreadCrumbs
         visibleBreadCrumbs={visibleBreadCrumbs}
         collapsedBreadCrumbs={wrappedBreadcrumbs}
         listRef={listRef}
         {...staticProps}
      />
   );
});

export function StaticBreadCrumbs({
   visibleBreadCrumbs,
   collapsedBreadCrumbs = [],
   breadcrumbIcon = <DefaultBreadcrumbIcon />,
   linkComponent = 'a',
   includeSchema = false,
   listRef,
   className,
   ...rest
}: StaticBreadCrumbsProps) {
   return (
      <div className={cn('flex h-12 shrink grow items-center gap-1.5', className)} {...rest}>
         <IFixitCollapsedBreadcrumb
            breadCrumbs={collapsedBreadCrumbs}
            breadcrumbIcon={breadcrumbIcon}
            linkComponent={linkComponent}
         />
         <IFixitBreadcrumb
            includeSchema={includeSchema}
            breadCrumbs={visibleBreadCrumbs}
            breadcrumbIcon={breadcrumbIcon}
            linkComponent={linkComponent}
            listRef={listRef}
         />
      </div>
   );
}

function DefaultBreadcrumbIcon() {
   return (
      <div className="flex">
         <FaIcon
            name="chevron-right"
            className="mt-px h-2.5 w-auto aspect-[320/512] text-gray-400"
         />
      </div>
   );
}

const IFixitBreadcrumb = memo(function IFixitBreadcrumb({
   breadCrumbs,
   breadcrumbIcon,
   linkComponent,
   listRef,
   includeSchema,
}: {
   breadCrumbs: BreadcrumbItem[];
   breadcrumbIcon: ReactElement;
   linkComponent: ElementType;
   listRef?: RefObject<HTMLOListElement | null>;
   includeSchema: boolean;
}) {
   const reversedBreadCrumbs = breadCrumbs.slice().reverse();

   const BreadCrumbItems = reversedBreadCrumbs.map((crumb, index) => {
      const isLastBreadCrumb = index === 0;
      return (
         <IFixitBreadcrumbItem
            key={crumb.label}
            {...crumb}
            // `index` walks the reversed list, deepest first, because the list
            // paints with `flex-row-reverse`. schema.org counts the other way,
            // from the top of the hierarchy, which is also how the server
            // placeholder in NavBreadcrumbsComponent numbers them.
            position={reversedBreadCrumbs.length - index}
            isLast={isLastBreadCrumb}
            includeSchema={includeSchema}
            linkComponent={linkComponent}
            breadcrumbIcon={breadcrumbIcon}
         />
      );
   });

   const breadcrumbSchema = includeSchema
      ? {
           itemScope: true,
           itemProp: 'breadcrumb',
           itemType: 'http://schema.org/BreadcrumbList',
        }
      : {};

   return (
      <nav aria-label="breadcrumb" {...breadcrumbSchema}>
         <ol
            ref={listRef}
            className={cn(
               hiddenWrapContainerClasses,
               'm-0 h-[1.5em] min-h-5 flex-row-reverse items-center justify-end p-0'
            )}
         >
            {BreadCrumbItems}
         </ol>
      </nav>
   );
}, isEqual);

const IFixitBreadcrumbItem = memo(function IFixitBreadcrumbItem({
   label,
   url,
   onClick,
   isLast,
   position,
   includeSchema,
   linkComponent,
   breadcrumbIcon,
}: IFixitBreadcrumbItemProps) {
   const breadcrumbItemSchema = includeSchema
      ? {
           itemScope: true,
           itemProp: 'itemListElement',
           itemType: 'http://schema.org/ListItem',
        }
      : {};

   const MetaTags = includeSchema ? (
      <>
         <meta itemProp="item" content={url} />
         <meta itemProp="name" content={label} />
         <meta itemProp="position" content={`${position}`} />
      </>
   ) : null;

   // An onClick-only crumb (the device picker's) gets a bare `a`, because a
   // `linkComponent` such as Next.js's `SmartLink` requires an `href`.
   const Label = url ? linkComponent : onClick ? 'a' : 'span';

   return (
      <li data-name={label} {...breadcrumbItemSchema} className="inline-flex items-center">
         <Label
            href={url}
            onClick={onClick}
            className={cn(
               'line-clamp-1 hover:no-underline',
               Label !== 'span' && 'cursor-pointer',
               isLast
                  ? 'font-medium text-gray-900 visited:text-gray-900'
                  : 'font-normal text-gray-500 visited:text-gray-500'
            )}
         >
            {label}
         </Label>
         {MetaTags}
         {!isLast && (
            <span role="presentation" className="mx-2">
               {breadcrumbIcon}
            </span>
         )}
      </li>
   );
}, isEqual);

const IFixitCollapsedBreadcrumb = memo(function IFixitCollapsedBreadcrumb({
   breadCrumbs,
   breadcrumbIcon,
   linkComponent,
}: {
   breadCrumbs: BreadcrumbItem[];
   breadcrumbIcon: ReactElement;
   linkComponent: ElementType;
}) {
   if (!breadCrumbs.length) {
      return null;
   }

   const CollapsedBreadCrumbItems = breadCrumbs.map(crumb => (
      <IFixitCollapsedBreadcrumbItem {...crumb} linkComponent={linkComponent} key={crumb.label} />
   ));

   return (
      <>
         <DropdownMenu>
            <DropdownMenuTrigger asChild>
               <button
                  type="button"
                  aria-label="More breadcrumbs"
                  // 576px, not Tailwind's `sm` (640px): the button widens one
                  // breakpoint before the icon grows.
                  className="mr-1 inline-flex h-6 min-w-[26px] shrink-0 items-center justify-center rounded bg-gray-300 hover:bg-gray-200 min-[576px]:min-w-8"
               >
                  <FaIcon
                     name="ellipsis"
                     className="h-3.5 w-auto aspect-[448/512] text-gray-500 sm:h-4"
                  />
               </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
               {CollapsedBreadCrumbItems.reverse()}
            </DropdownMenuContent>
         </DropdownMenu>
         {breadcrumbIcon}
      </>
   );
}, isEqual);

function IFixitCollapsedBreadcrumbItem({
   label,
   url,
   onClick,
   linkComponent,
}: BreadcrumbItem & { linkComponent: ElementType }) {
   const itemClassName = 'text-sm text-gray-900';
   if (!url && !onClick) {
      return <DropdownMenuItem className={itemClassName}>{label}</DropdownMenuItem>;
   }
   // Same rule as IFixitBreadcrumbItem's `Label`, inline in both because a
   // helper returning the element type trips react-hooks/static-components.
   const Link = url ? linkComponent : 'a';
   return (
      <DropdownMenuItem asChild>
         <Link
            href={url}
            onClick={onClick}
            className={cn(itemClassName, 'visited:text-gray-900 hover:no-underline')}
         >
            {label}
         </Link>
      </DropdownMenuItem>
   );
}
